import React, { useState, useEffect } from 'react';
import { Search, MoreVertical, Shield, Crown, User, Ban, CheckCircle } from 'lucide-react';
import { usersAPI } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield size={14} className="text-red-400" />;
      case 'paid':
        return <Crown size={14} className="text-yellow-400" />;
      default:
        return <User size={14} className="text-gray-400" />;
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-red-600',
      paid: 'bg-yellow-600',
      free: 'bg-gray-600',
    };
    return (
      <Badge className={`${colors[role] || 'bg-gray-600'} text-white`}>
        <span className="flex items-center gap-1">
          {getRoleIcon(role)}
          {role?.charAt(0).toUpperCase() + role?.slice(1)}
        </span>
      </Badge>
    );
  };

  const handleToggleStatus = async (userId) => {
    try {
      await usersAPI.toggleStatus(userId);
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to toggle status');
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await usersAPI.updateRole(userId, newRole);
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to change role');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Manage Users</h1>
          <p className="text-gray-400">View and manage user accounts</p>
        </div>
        <Button className="bg-purple-600 hover:bg-purple-700">
          Export Users
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
        <Input
          type="text"
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-[#1a1a2e] border-gray-700 text-white placeholder:text-gray-500"
        />
      </div>

      {/* Users Table */}
      <div className="bg-[#1a1a2e] rounded-xl border border-gray-800 overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-gray-800 hover:bg-transparent">
                <TableHead className="text-gray-400">User</TableHead>
                <TableHead className="text-gray-400">Role</TableHead>
                <TableHead className="text-gray-400">Package</TableHead>
                <TableHead className="text-gray-400">Credits</TableHead>
                <TableHead className="text-gray-400">Status</TableHead>
                <TableHead className="text-gray-400">Joined</TableHead>
                <TableHead className="text-gray-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id} className="border-gray-800 hover:bg-gray-800/50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-semibold">
                        {user.avatar || user.name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <p className="text-white font-medium">{user.name}</p>
                        <p className="text-gray-500 text-sm">{user.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell className="text-white">{user.package || 'Free'}</TableCell>
                  <TableCell className="text-white">{user.credits || 0}</TableCell>
                  <TableCell>
                    <Badge className={user.is_active !== false ? 'bg-green-600' : 'bg-red-600'}>
                      {user.is_active !== false ? 'active' : 'suspended'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-gray-400">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
                          <MoreVertical size={18} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#1a1a2e] border-gray-700">
                        <DropdownMenuItem 
                          className="text-white hover:bg-purple-600/30 cursor-pointer"
                          onClick={() => handleChangeRole(user.id, 'admin')}
                        >
                          <Shield size={14} className="mr-2 text-red-400" />
                          Make Admin
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-white hover:bg-purple-600/30 cursor-pointer"
                          onClick={() => handleChangeRole(user.id, 'paid')}
                        >
                          <Crown size={14} className="mr-2 text-yellow-400" />
                          Make Premium
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-white hover:bg-purple-600/30 cursor-pointer"
                          onClick={() => handleChangeRole(user.id, 'free')}
                        >
                          <User size={14} className="mr-2" />
                          Make Free
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-white hover:bg-purple-600/30 cursor-pointer"
                          onClick={() => handleToggleStatus(user.id)}
                        >
                          {user.is_active !== false ? (
                            <><Ban size={14} className="mr-2 text-red-400" /> Suspend</>
                          ) : (
                            <><CheckCircle size={14} className="mr-2 text-green-400" /> Activate</>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default ManageUsers;
